import { __rest } from "tslib";
import { action } from '@sas/nova-commons/action';
import { idSliceSymbol } from './utils/action/createElementSlice';
function getIds(key, state) {
    var _a;
    let currentKeys = [key];
    const childKeys = (_a = state.byId[key]) === null || _a === void 0 ? void 0 : _a.childKeys;
    if (!childKeys) {
        return currentKeys;
    }
    for (const child of childKeys) {
        currentKeys = currentKeys.concat(getIds(child, state));
    }
    return currentKeys;
}
function deleteIdAndChildrenImpl(state, ids, reachedNodes) {
    let newState = undefined;
    reachedNodes.add(state);
    if (idSliceSymbol in state) {
        for (const id of ids) {
            if (!state[id]) {
                continue;
            }
            if (!newState) {
                newState = Object.assign({}, state);
            }
            delete newState[id];
        }
    }
    else {
        for (const [key, val] of Object.entries(state)) {
            if (reachedNodes.has(val)) {
                console.warn('Unexpected circular dependency in state. Skipping reached node.');
                continue;
            }
            if (val instanceof Object) {
                const newVal = deleteIdAndChildrenImpl(val, ids, reachedNodes);
                if (val !== newVal) {
                    if (!newState) {
                        newState = Object.assign({}, state);
                    }
                    newState[key] = newVal;
                }
            }
        }
    }
    reachedNodes.delete(state);
    return newState || state;
}
function deleteIdAndChildren(state, id) {
    var _a;
    const ids = getIds(id, state.ui.birdLayout);
    let newState = deleteIdAndChildrenImpl(state, ids, new Set());
    let stateChanged = false;
    for (const [parentId, value] of Object.entries(newState.ui.birdLayout.byId)) {
        if ((_a = value === null || value === void 0 ? void 0 : value.childKeys) === null || _a === void 0 ? void 0 : _a.includes(id)) {
            if (!stateChanged) {
                stateChanged = true;
                // support shallow compares
                newState = Object.assign(Object.assign({}, newState), { ui: Object.assign(Object.assign({}, newState.ui), { birdLayout: Object.assign(Object.assign({}, newState.ui.birdLayout), { byId: Object.assign({}, newState.ui.birdLayout.byId) }) }) });
            }
            newState.ui.birdLayout.byId[parentId] = Object.assign(Object.assign({}, value), { childKeys: value === null || value === void 0 ? void 0 : value.childKeys.filter((val) => val !== id) });
            // Only one container should own a child
            break;
        }
    }
    if (newState.bird.maximizedVisual && ids.includes(newState.bird.maximizedVisual)) {
        newState = Object.assign(Object.assign({}, newState), { bird: Object.assign(Object.assign({}, newState.bird), { maximizedVisual: null }) });
    }
    return newState;
}
const AT = {
    DeleteReportObject: 'root/DeleteReportObject',
    DeleteSection: 'root/DeleteSection',
};
const actions = {
    deleteReportObject: (objectId) => action(AT.DeleteReportObject, objectId),
    deleteSection: (sectionId) => action(AT.DeleteSection, sectionId),
};
export const RootState = {
    actions,
    actionTypes: AT,
    reducer: (state, reducerAction) => {
        var _a, _b;
        const action = reducerAction;
        switch (action.type) {
            case AT.DeleteReportObject: {
                return deleteIdAndChildren(state, action.payload);
            }
            case AT.DeleteSection: {
                const id = action.payload;
                const sectionData = state.sections.byId[id];
                const newState = Object.assign(Object.assign({}, state), { sections: Object.assign({}, state.sections), ui: Object.assign(Object.assign({}, state.ui), { birdLayout: Object.assign({}, state.ui.birdLayout) }) });
                const _c = state.sections.byId, _d = id, _ = _c[_d], byId = __rest(_c, [typeof _d === "symbol" ? _d : _d + ""]);
                newState.sections.byId = byId;
                const sectionTabOrder = state.sections.sectionTabOrder.filter((sectionId) => sectionId !== id);
                newState.sections.sectionTabOrder = sectionTabOrder;
                newState.sections.transitioning = false;
                if (newState.sections.nextSection !== undefined) {
                    newState.sections.currentSection = newState.sections.nextSection;
                    newState.sections.nextSection = undefined;
                }
                if (newState.sections.currentSection === id) {
                    const newIndex = Math.max(0, newState.sections.sectionTabOrder.indexOf(id) - 1);
                    newState.sections.currentSection =
                        (_a = newState.sections.sectionTabOrder[newIndex]) !== null && _a !== void 0 ? _a : undefined;
                }
                const rootContainerId = typeof newState.sections.currentSection === 'string'
                    ? (_b = newState.sections.byId[newState.sections.currentSection]) === null || _b === void 0 ? void 0 : _b.rootContainerId
                    : undefined;
                if (rootContainerId !== undefined) {
                    newState.ui.birdLayout.rootIds = [rootContainerId];
                }
                else {
                    newState.ui.birdLayout.rootIds = [];
                }
                if ((sectionData === null || sectionData === void 0 ? void 0 : sectionData.rootContainerId) !== undefined) {
                    const headerState = deleteIdAndChildren(newState, `${sectionData.rootContainerId}-header`);
                    return deleteIdAndChildren(headerState, sectionData.rootContainerId);
                }
                return newState;
            }
        }
        return state;
    },
};
